import cn from "classnames"
import { FieldErrors, UseFormRegister, UseFormWatch } from "react-hook-form"
import type { OrdersGeneralInfo } from "../../pages/OrderPage"

const MAX_ALLOWED_PROVINCE_LENGTH = 2
const MAX_ALLOWED_SDI_CODE_LENGTH = 7
const MAX_ALLOWED_VAT_NUMBER_LENGTH = 13
const MAX_ALLOWED_TAX_CODE_LENGTH = 16

interface BusinessBillingFormProps {
  register: UseFormRegister<OrdersGeneralInfo>
  errors: FieldErrors<OrdersGeneralInfo>
  watch: UseFormWatch<OrdersGeneralInfo>
}

export default function BusinessBillingForm({ register, errors, watch }: BusinessBillingFormProps) {
  const billingErrors = errors.businessBillingDetails

  const atLeastOneTaxId = () =>
    !!watch("businessBillingDetails.vatNumber") ||
    !!watch("businessBillingDetails.taxCode") ||
    "Inserisci la Partita IVA o il Codice Fiscale"

  return (
    <div className="row">
      <div className="col-12 mb-3">
        <label htmlFor="businessBillingDetails.companyName" className="form-label">
          Ragione Sociale
        </label>
        <input
          id="businessBillingDetails.companyName"
          placeholder="Freeloop"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.companyName })}
          {...register("businessBillingDetails.companyName", { required: "Inserisci la ragione sociale" })}
        />
        {billingErrors?.companyName && <div className="invalid-feedback">{billingErrors.companyName.message}</div>}
      </div>

      <div className="col-12 mb-3">
        <label htmlFor="businessBillingDetails.address" className="form-label">
          Indirizzo
        </label>
        <input
          id="businessBillingDetails.address"
          placeholder="Via Roma 123"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.address })}
          {...register("businessBillingDetails.address", { required: "Inserisci l'indirizzo" })}
        />
        {billingErrors?.address && <div className="invalid-feedback">{billingErrors.address.message}</div>}
      </div>

      <div className="col-12 col-sm-4 mb-3">
        <label htmlFor="businessBillingDetails.zipCode" className="form-label">
          CAP
        </label>
        <input
          id="businessBillingDetails.zipCode"
          placeholder="20121"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.zipCode })}
          {...register("businessBillingDetails.zipCode", { required: "Inserisci il CAP" })}
        />
        {billingErrors?.zipCode && <div className="invalid-feedback">{billingErrors.zipCode.message}</div>}
      </div>

      <div className="col-12 col-sm-5 mb-3">
        <label htmlFor="businessBillingDetails.city" className="form-label">
          Città
        </label>
        <input
          id="businessBillingDetails.city"
          placeholder="Milano"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.city })}
          {...register("businessBillingDetails.city", { required: "Inserisci la città" })}
        />
        {billingErrors?.city && <div className="invalid-feedback">{billingErrors.city.message}</div>}
      </div>

      <div className="col-12 col-sm-3 mb-3">
        <label htmlFor="businessBillingDetails.province" className="form-label">
          Provincia
        </label>
        <input
          id="businessBillingDetails.province"
          placeholder="MI"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.province })}
          {...register("businessBillingDetails.province", {
            required: "Inserisci la provincia",
            maxLength: {
              value: MAX_ALLOWED_PROVINCE_LENGTH,
              message: `La provincia può avere al massimo ${MAX_ALLOWED_PROVINCE_LENGTH} caratteri`,
            },
          })}
        />
        {billingErrors?.province && <div className="invalid-feedback">{billingErrors.province.message}</div>}
      </div>

      <div className="col-12 mb-3">
        <label htmlFor="businessBillingDetails.country" className="form-label">
          Paese
        </label>
        <input
          id="businessBillingDetails.country"
          placeholder="Italia"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.country })}
          {...register("businessBillingDetails.country", { required: "Inserisci il paese" })}
        />
        {billingErrors?.country && <div className="invalid-feedback">{billingErrors.country.message}</div>}
      </div>

      <div className="col-12 col-sm-6 mb-3">
        <label htmlFor="businessBillingDetails.vatNumber" className="form-label">
          Partita IVA
        </label>
        <input
          id="businessBillingDetails.vatNumber"
          placeholder="IT12345678901"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.vatNumber })}
          {...register("businessBillingDetails.vatNumber", {
            validate: atLeastOneTaxId,
            maxLength: {
              value: MAX_ALLOWED_VAT_NUMBER_LENGTH,
              message: `La Partita IVA può avere al massimo ${MAX_ALLOWED_VAT_NUMBER_LENGTH} caratteri`,
            },
          })}
        />
        {billingErrors?.vatNumber && <div className="invalid-feedback">{billingErrors.vatNumber.message}</div>}
      </div>

      <div className="col-12 col-sm-6 mb-3">
        <label htmlFor="businessBillingDetails.taxCode" className="form-label">
          Codice Fiscale
        </label>
        <input
          id="businessBillingDetails.taxCode"
          placeholder="RSSMRA80A01F205X"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.taxCode })}
          {...register("businessBillingDetails.taxCode", {
            validate: atLeastOneTaxId,
            maxLength: {
              value: MAX_ALLOWED_TAX_CODE_LENGTH,
              message: `Il Codice Fiscale può avere al massimo ${MAX_ALLOWED_TAX_CODE_LENGTH} caratteri`,
            },
          })}
        />
        {billingErrors?.taxCode && <div className="invalid-feedback">{billingErrors.taxCode.message}</div>}
      </div>

      <div className="col-12 col-sm-6 mb-3">
        <label htmlFor="businessBillingDetails.sdiCode" className="form-label">
          Codice SDI
        </label>
        <input
          id="businessBillingDetails.sdiCode"
          placeholder="ABCDEFG"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.sdiCode })}
          {...register("businessBillingDetails.sdiCode", {
            required: "Inserisci il codice SDI",
            maxLength: {
              value: MAX_ALLOWED_SDI_CODE_LENGTH,
              message: `Il codice SDI può avere al massimo ${MAX_ALLOWED_SDI_CODE_LENGTH} caratteri`,
            },
          })}
        />
        {billingErrors?.sdiCode && <div className="invalid-feedback">{billingErrors.sdiCode.message}</div>}
      </div>

      <div className="col-12 col-sm-6 mb-3">
        <label htmlFor="businessBillingDetails.billingEmail" className="form-label">
          Email di fatturazione
        </label>
        <input
          id="businessBillingDetails.billingEmail"
          placeholder="billing@acme.com"
          type="text"
          className={cn("form-control", { "is-invalid": billingErrors?.billingEmail })}
          {...register("businessBillingDetails.billingEmail", {
            required: "Inserisci l'email di fatturazione",
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              message: "Inserisci un indirizzo email valido",
            },
          })}
        />
        {billingErrors?.billingEmail && <div className="invalid-feedback">{billingErrors.billingEmail.message}</div>}
      </div>
    </div>
  )
}
